import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Reveal } from "./motion";
import { api } from "../lib/api";

const empty = {
  name: "",
  email: "",
  phone: "",
  company: "",
  team_size: "",
  need: "",
  current_peo: "",
  renewal_date: "",
  states: "",
  message: "",
};
const helpOptions = [
  "Group health plan guidance",
  "Ancillary & voluntary benefits",
  "Level-funded / alternative funding",
  "Renewal or repricing review",
  "Benefits education & enrollment",
  "PEO evaluation or optimization",
  "Not sure yet — let's talk",
];

const inputCls =
  "rounded-none border-border bg-cream px-4 py-3 text-sm text-ink placeholder:text-ink/35 focus-visible:ring-1 focus-visible:ring-forest focus-visible:ring-offset-0";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60";
const selectCls =
  "h-10 w-full rounded-none border border-border bg-cream px-4 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-forest";

export const PEOForm = () => {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", { ...form, type: "peo" });
      toast.success("Request received — I'll reach out within one business day.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <Reveal delay={0.1}>
      <div className="mt-16 border border-border bg-cream p-8 lg:p-12" data-testid="employers-plan-check-cta">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra">2-Minute Plan Check</p>
        <h2 className="mt-4 max-w-3xl font-serif text-3xl font-light leading-[1.1] tracking-tight text-forest sm:text-4xl">
          Not sure which health plan setup fits?
        </h2>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink/70">
          Answer a few quick questions and see which funding options, from traditional group plans to
          level-funded, PEO and ICHRA, are likely to fit your company.
        </p>
        <Link
          to="/plan-check?utm_source=website&utm_medium=employers_page"
          data-testid="employers-plan-check-button"
          className="group mt-8 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss"
        >
          Start the Plan Check
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={2} />
        </Link>
      </div>
    </Reveal>
    <Reveal delay={0.15}>
      <div className="mt-16 border border-border bg-sand/40 p-8 lg:p-12" data-testid="employers-cta">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra">No Cost to You · Carriers Pay My Commission</p>
        <h2 className="mt-4 max-w-3xl font-serif text-3xl font-light leading-[1.1] tracking-tight text-forest sm:text-4xl">
          Tell me what your team <em className="text-terra">needs</em>.
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/65">
          One form for every employer request — group health, ancillary, funding, renewals, enrollment help, or
          a PEO review. Only the first few fields are required; the PEO questions are optional and only matter
          if that&rsquo;s your situation.
        </p>

        <form onSubmit={submit} className="mt-10" data-testid="peo-form">
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="peo-name" className={labelCls}>Full Name *</Label>
              <Input id="peo-name" required value={form.name} onChange={set("name")} placeholder="Jane Doe" className={inputCls} data-testid="peo-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="peo-email" className={labelCls}>Work Email *</Label>
              <Input id="peo-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@company.com" className={inputCls} data-testid="peo-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="peo-company" className={labelCls}>Company *</Label>
              <Input id="peo-company" required value={form.company} onChange={set("company")} placeholder="Acme Co." className={inputCls} data-testid="peo-company-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="peo-phone" className={labelCls}>Phone (optional)</Label>
              <Input id="peo-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(713) 555-0100" className={inputCls} data-testid="peo-phone-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="peo-team" className={labelCls}>Number of Employees *</Label>
              <select id="peo-team" required value={form.team_size} onChange={set("team_size")} className={selectCls} data-testid="peo-team-size-select">
                <option value="">Select…</option>
                <option value="1-10">1–10</option>
                <option value="11-25">11–25</option>
                <option value="26-50">26–50</option>
                <option value="51-150">51–150</option>
                <option value="150+">150+</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="peo-need" className={labelCls}>What Can I Help With? *</Label>
              <select id="peo-need" required value={form.need} onChange={set("need")} className={selectCls} data-testid="peo-need-select">
                <option value="">Select…</option>
                {helpOptions.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="peo-message" className={labelCls}>Anything I should know? *</Label>
              <Textarea
                id="peo-message"
                required
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="e.g. We're adding 12 people this fall, renewal came in 14% higher, or we've never offered benefits before…"
                className={`${inputCls} resize-none`}
                data-testid="peo-message-input"
              />
            </div>
          </div>

          <div className="mt-10 border-t border-border pt-8" data-testid="peo-optional-block">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/45">
              Optional · Only if you use or are considering a PEO
            </p>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="peo-current" className={labelCls}>Current PEO (optional)</Label>
                <Input id="peo-current" value={form.current_peo} onChange={set("current_peo")} placeholder="Leave blank if you don't have one" className={inputCls} data-testid="peo-current-peo-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="peo-renewal" className={labelCls}>Renewal / Decision Timeline (optional)</Label>
                <select id="peo-renewal" value={form.renewal_date} onChange={set("renewal_date")} className={selectCls} data-testid="peo-renewal-select">
                  <option value="">Select…</option>
                  <option value="within-3-months">Within 3 months</option>
                  <option value="3-6-months">3–6 months</option>
                  <option value="6-12-months">6–12 months</option>
                  <option value="just-exploring">Just exploring</option>
                </select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="peo-states" className={labelCls}>States Where Employees Work (optional)</Label>
                <Input id="peo-states" value={form.states} onChange={set("states")} placeholder="e.g. TX, OK, remote in FL" className={inputCls} data-testid="peo-states-input" />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={sending}
            data-testid="peo-submit-button"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-moss disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                Request a Consultation
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </>
            )}
          </button>
          <p className="mt-5 text-xs leading-relaxed text-ink/50" data-testid="peo-privacy-note">
            Your details are used only to prepare for your consultation. No spam, no list-sharing — ever.
          </p>
        </form>
      </div>
    </Reveal>
    </>
  );
};
